// frontend/src/pages/CarRental.jsx
import React from "react";
import bmw from "../assets/rentCar/bmw.jpeg";
import toyota from "../assets/rentCar/Untitled.jpeg";
import audi from "../assets/rentCar/audi.jpeg";
import ford from "../assets/rentCar/ford.jpeg";
import honda from "../assets/rentCar/honda.jpeg";
import hyundai from "../assets/rentCar/hyundai.jpeg";
import mercidez from "../assets/rentCar/mercidezbenze.jpeg";
import nissan from "../assets/rentCar/nissan.jpeg";
import {
    Car,
    Package,
    Clock,
    Users,
    CheckCircle,
    Shield,
    ArrowRight,
} from "lucide-react";

const CarRental = ({ setForm }) => {
    // Available Vehicles
    const vehicles = [
        {
            id: 1,
            name: "Honda City",
            brand: "Honda",
            type: "Sedan",
            capacity: "4 passengers",
            luggage: "2 medium bags",
            features: ["AC", "Comfort Seats", "Fuel Efficient", "GPS"],
            image: honda,
            color: "from-blue-400 to-cyan-400",
        },
        {
            id: 2,
            name: "Hyundai Creta",
            brand: "Hyundai",
            type: "SUV",
            capacity: "5 passengers",
            luggage: "3 medium bags",
            features: ["AC", "Spacious", "Touchscreen", "Cruise Control"],
            image: hyundai,
            color: "from-emerald-400 to-teal-400",
        },
        {
            id: 3,
            name: "Audi A4",
            brand: "Audi",
            type: "Luxury Sedan",
            capacity: "4 passengers",
            luggage: "2 medium bags",
            features: [
                "Luxury Interior",
                "Leather Seats",
                "GPS",
                "Climate Control",
            ],
            image: audi,
            color: "from-purple-400 to-pink-400",
        },
        {
            id: 4,
            name: "BMW 5 Series",
            brand: "BMW",
            type: "Luxury Sedan",
            capacity: "4 passengers",
            luggage: "3 medium bags",
            features: [
                "Premium Interior",
                "Comfort Seats",
                "Entertainment System",
            ],
            image: bmw,
            color: "from-indigo-400 to-blue-500",
        },
        {
            id: 5,
            name: "Ford Endeavour",
            brand: "Ford",
            type: "SUV",
            capacity: "7 passengers",
            luggage: "4 large bags",
            features: ["Spacious", "Off-road Ready", "Climate Control"],
            image: ford,
            color: "from-gray-400 to-slate-500",
        },
        {
            id: 6,
            name: "Mercedes-Benz E-Class",
            brand: "Mercedes-Benz",
            type: "Luxury Sedan",
            capacity: "4 passengers",
            luggage: "3 medium bags",
            features: ["Luxury Interior", "Ambient Lighting", "Comfort Seats"],
            image: mercidez,
            color: "from-yellow-400 to-amber-500",
        },
        {
            id: 7,
            name: "Nissan X-Trail",
            brand: "Nissan",
            type: "SUV",
            capacity: "5 passengers",
            luggage: "3 medium bags",
            features: ["Spacious", "AC", "Safety Features"],
            image: nissan,
            color: "from-red-400 to-rose-500",
        },
        {
            id: 8,
            name: "Toyota Innova Crysta",
            brand: "Toyota",
            type: "MPV",
            capacity: "7 passengers",
            luggage: "4 large bags",
            features: ["Spacious", "Comfort Seats", "Climate Control"],
            image: toyota,
            color: "from-green-400 to-lime-500",
        },
    ];

    return (
        <div className="min-h-screen">
            {/* Premium Experience Section */}
            <section className="py-20 sm:pt-0">
                <div className="container mx-auto px-4">
                    {" "}
                    {/* Fixed: changed "contain" to "container" */}
                    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
                        {/* Left Content */}
                        <div>
                            <span className="block mb-4 text-sm font-semibold tracking-wide bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-white uppercase sm:text-center">
                                Premium Car Rental
                            </span>

                            <h2 className="text-4xl font-extrabold text-white leading-tight mb-6 sm:text-center">
                                A Smarter, Simpler Way <br />
                                to Rent a Car
                            </h2>

                            <p className="text-lg text-white mb-8 sm:text-center">
                                From luxury sedans to spacious SUVs, we deliver
                                a seamless car rental experience designed for
                                comfort, reliability, and peace of mind.
                            </p>

                            <div className="space-y-5">
                                <div className="flex items-start gap-4 p-4 rounded-xl transition">
                                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 text-white shadow-md">
                                        <Car className="h-6 w-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-white">
                                            Premium Fleet
                                        </h3>
                                        <p className="text-white text-sm ">
                                            Well-maintained vehicles with latest
                                            features and comfort amenities
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4 p-4 rounded-xl transition">
                                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-md">
                                        <Users className="h-6 w-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-white">
                                            Professional Drivers
                                        </h3>
                                        <p className="text-white text-sm leading-relaxed">
                                            Experienced, verified drivers with
                                            excellent route knowledge
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4 p-4 rounded-xl transition">
                                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-md">
                                        <Shield className="h-6 w-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-white">
                                            All-Inclusive Pricing
                                        </h3>
                                        <p className="text-white text-sm leading-relaxed">
                                            No hidden charges. Fuel, parking,
                                            tolls all included in package
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4 p-4 rounded-xl transition">
                                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-md">
                                        <Clock className="h-6 w-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-white">
                                            24/7 Availability
                                        </h3>
                                        <p className="text-white text-sm leading-relaxed">
                                            Book anytime, travel anytime with
                                            round-the-clock support
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Visual */}
                        <div className="relative">
                            <div className="absolute -top-10 -left-10 w-72 h-72 bg-orange-200 rounded-full blur-3xl opacity-40"></div>
                            <div className="absolute -bottom-10 -right-10 w-72 h-72 bg-amber-200 rounded-full blur-3xl opacity-40"></div>

                            <div className="relative bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-gray-200">
                                <img
                                    src="https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=800&h=600&fit=crop"
                                    alt="Luxury Car"
                                    className="w-full h-64 object-cover rounded-2xl"
                                />

                                <div className="mt-6 text-center">
                                    <h4 className="text-xl font-bold text-gray-900">
                                        Drive Comfort. Drive Confidence.
                                    </h4>
                                    <p className="text-gray-600 text-sm mt-2">
                                        Trusted by thousands of happy travelers
                                        across the country.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Available Vehicles Section */}
            <section className="py-16">
                <div className="container mx-auto px-6">
                    <div className="max-w-6xl mx-auto">
                        {/* Section Header */}
                        <div className="text-center mb-16">
                            <h2
                                className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text 
                       bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 mb-4"
                            >
                                All Vehicles Available
                            </h2>
                            <p className="text-gray-300 max-w-2xl mx-auto text-lg md:text-xl leading-relaxed">
                                Choose from our fleet of well-maintained premium
                                vehicles and experience luxury travel at its
                                finest.
                            </p>
                        </div>

                        {/* Vehicles Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
                            {vehicles.map((vehicle) => (
                                <div
                                    key={vehicle.id}
                                    className="relative bg-white/5 backdrop-blur-xl rounded-3xl overflow-hidden shadow-[0_25px_60px_rgba(0,0,0,0.8)]
                       transform transition-all duration-500 hover:scale-105 hover:shadow-2xl group"
                                >
                                    {/* Image with overlay & tilt effect */}
                                    <div className="relative h-56 overflow-hidden rounded-2xl">
                                        <img
                                            src={vehicle.image}
                                            alt={vehicle.name}
                                            className="w-full h-full transform transition-transform duration-500 group-hover:scale-110 group-hover:rotate-1"
                                        />
                                        <div className="absolute bottom-0 left-0 p-4">
                                            <h3 className="text-white text-xl font-bold">
                                                {vehicle.name}
                                            </h3>
                                            <p className="text-gray-200 text-sm">
                                                {vehicle.brand} • {vehicle.type}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="p-6 space-y-4">
                                        {/* Info */}
                                        <div className="grid grid-cols-2 gap-4 text-gray-300 text-sm">
                                            <div>
                                                <span className="font-semibold">
                                                    Capacity:
                                                </span>
                                                <p>{vehicle.capacity}</p>
                                            </div>
                                            <div>
                                                <span className="font-semibold">
                                                    Luggage:
                                                </span>
                                                <p>{vehicle.luggage}</p>
                                            </div>
                                        </div>

                                        {/* Features */}
                                        <div>
                                            <h4 className="font-semibold text-white mb-2">
                                                Features
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {vehicle.features.map(
                                                    (feature, index) => (
                                                        <span
                                                            key={index}
                                                            className="px-3 py-1 rounded-full text-xs font-medium
                                 bg-gradient-to-r from-green-400 via-blue-400 to-purple-500 text-white shadow-md"
                                                        >
                                                            {feature}
                                                        </span>
                                                    )
                                                )}
                                            </div>
                                        </div>

                                        {/* Book Button */}
                                        <button
                                            onClick={() => setForm(true)}
                                            className="w-full mt-4 bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600
                                                text-white py-3 px-8 rounded-3xl font-semibold shadow-2xl hover:scale-105
                                                transform transition-all duration-300 flex items-center justify-center group"
                                        >
                                            <Car className="mr-3 h-5 w-5" />
                                            Book Now
                                            <ArrowRight className="ml-3 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                                        </button>
                                    </div>

                                    {/* Decorative Glows */}
                                    <div className="absolute -top-8 -right-8 w-32 h-32 bg-pink-500/20 rounded-full blur-3xl animate-blob"></div>
                                    <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Custom CSS */}
            <style>{`

            /* Prevent horizontal scroll */
                body
                {
                    overflow-x: hidden;
                    max-width: 100%;
                }


            `}</style>
        </div>
    );
};

export default CarRental;
